import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { CreditCard } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { LocalizedName, localizedName } from '@/components/localized-name';
import { usePermission } from '@/features/auth/auth.store';
import { paymentMethodLabelKey } from '@/features/events/payment-methods';
import {
  formatCurrency,
  formatDate,
  formatDateTime,
  formatNumber,
  formatPhone,
} from '@/lib/format';
import {
  useRefundSubscriptionPayment,
  useSubscriptionDetail,
  useVoidSubscriptionPayment,
} from './subscriptions.api';
import { originLabelKey } from './subscription-cells';
import {
  PAYMENT_STATUS,
  type SubscriptionPayment,
  type SubscriptionRow,
} from './types';

/**
 * Everything about one subscription, filled by a single
 * `GET v2/admin/memberships/{id}`.
 *
 * The page this replaces made the admin press "Load payments" and "Load
 * benefits" per row before any of it appeared, so a row with no visible payment
 * was indistinguishable from one nobody had loaded.
 */

/** One summary line — label above value, so a 360px screen never squeezes it. */
function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="grid gap-0.5 border-b py-2.5 last:border-0 sm:grid-cols-[10rem_1fr] sm:gap-4">
      <p className="text-muted-foreground text-xs font-semibold">{label}</p>
      <div className="text-sm">{children}</div>
    </div>
  );
}

/**
 * A payment's outcome, in one badge.
 *
 * Three states that read very differently — a void records who did it, a refund
 * went back through the provider, a failure is money that never arrived.
 */
function PaymentBadge({ payment }: { payment: SubscriptionPayment }) {
  const { t } = useTranslation();

  if (payment.voidedAt) {
    return (
      <Badge variant="secondary">
        {t('manageSubscriptions.voided')}
        {payment.voidedByName ? ` · ${payment.voidedByName}` : ''}
      </Badge>
    );
  }
  if (payment.refunded) {
    return <Badge variant="outline">{t('manageSubscriptions.refunded')}</Badge>;
  }
  return payment.status === PAYMENT_STATUS.successful ? (
    <Badge variant="success">{t('manageSubscriptions.successful')}</Badge>
  ) : (
    <Badge variant="destructive">{t('manageSubscriptions.failed')}</Badge>
  );
}

function EmptyList({ children }: { children: ReactNode }) {
  return (
    <p className="text-muted-foreground rounded-xl border border-dashed py-10 text-center text-sm">
      {children}
    </p>
  );
}

export function SubscriptionDetailSheet({
  subscriptionId,
  open,
  onOpenChange,
  onRecordPayment,
}: {
  subscriptionId: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onRecordPayment: (row: SubscriptionRow) => void;
}) {
  const { t, i18n } = useTranslation();
  const language = i18n.resolvedLanguage ?? 'en';
  const canManageMoney = usePermission('isFinance');

  const detail = useSubscriptionDetail(open ? subscriptionId : null);
  const voidPayment = useVoidSubscriptionPayment(subscriptionId);
  const refundPayment = useRefundSubscriptionPayment(subscriptionId);
  const busy = voidPayment.isPending || refundPayment.isPending;

  const membership = detail.data?.membership ?? null;
  const payments = detail.data?.payments ?? [];
  const benefits = detail.data?.remainingBenefits ?? [];
  const redemptions = detail.data?.redemptions ?? [];
  const bookings = detail.data?.recentBookings ?? [];
  const members = detail.data?.members ?? [];

  // Both of these previously succeeded silently, which is its own kind of
  // unclear — the row changed but nothing said so.
  const done = (key: string) => () => toast.success(t(key));
  const failed = () => toast.error(t('manageSubscriptions.actionFailed'));

  /**
   * Recorded by hand → void it. Taken by a provider → the money has to go back
   * through the provider, so a refund is the only option.
   */
  function PaymentAction({ payment }: { payment: SubscriptionPayment }) {
    if (payment.voidedAt || !canManageMoney) return null;

    const refundable =
      !payment.refunded && payment.status === PAYMENT_STATUS.successful;

    return (
      <AlertDialog>
        <AlertDialogTrigger asChild>
          <Button
            variant="outline"
            size="sm"
            disabled={busy || (!payment.isOffline && !refundable)}
          >
            {payment.isOffline
              ? t('manageSubscriptions.void')
              : t('manageSubscriptions.refund')}
          </Button>
        </AlertDialogTrigger>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {payment.isOffline
                ? t('manageSubscriptions.void')
                : t('manageSubscriptions.refund')}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {payment.isOffline
                ? t('manageSubscriptions.voidPaymentPrompt')
                : t('manageSubscriptions.refundPaymentPrompt')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => {
                if (payment.isOffline) {
                  voidPayment.mutate(payment.id, {
                    onSuccess: done('manageSubscriptions.paymentVoided'),
                    onError: failed,
                  });
                } else {
                  refundPayment.mutate(payment.id, {
                    onSuccess: done('manageSubscriptions.paymentRefunded'),
                    onError: failed,
                  });
                }
              }}
            >
              {t('common.yes')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    );
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-full gap-0 overflow-y-auto p-0 sm:max-w-3xl"
      >
        <SheetHeader className="border-b pr-12">
          <SheetTitle>
            {membership?.user?.completeName ??
              t('manageSubscriptions.subscription')}
          </SheetTitle>
        </SheetHeader>

        <div className="p-4">
          {detail.isLoading && (
            <div className="space-y-4">
              <Skeleton className="h-9 w-64" />
              <div className="space-y-2">
                {Array.from({ length: 6 }).map((_, index) => (
                  <div key={index} className="flex justify-between gap-4">
                    <Skeleton className="h-4 w-24" />
                    <Skeleton className="h-4 w-40" />
                  </div>
                ))}
              </div>
            </div>
          )}

          {(detail.isError || (detail.data && !membership)) && (
            <p className="text-muted-foreground py-16 text-center text-sm">
              {t('manageSubscriptions.detailFailed')}
            </p>
          )}

          {membership && (
            <Tabs defaultValue="summary">
              <TabsList>
                <TabsTrigger value="summary">
                  {t('manageSubscriptions.summary')}
                </TabsTrigger>
                <TabsTrigger value="payments">
                  {t('manageSubscriptions.payments')} ({payments.length})
                </TabsTrigger>
                <TabsTrigger value="benefits">
                  {t('manageSubscriptions.benefits')}
                </TabsTrigger>
                <TabsTrigger value="bookings">
                  {t('manageSubscriptions.bookings')} ({bookings.length})
                </TabsTrigger>
                {members.length > 0 && (
                  <TabsTrigger value="family">
                    {t('manageSubscriptions.family')} ({members.length})
                  </TabsTrigger>
                )}
              </TabsList>

              {/* ------------------------------------------------ summary */}
              <TabsContent value="summary">
                <div className="rounded-xl border px-4">
                  <Row label={t('manageSubscriptions.user')}>
                    <p>{membership.user?.completeName}</p>
                    <p className="text-muted-foreground text-xs">
                      {membership.user?.email}
                    </p>
                    <p className="text-muted-foreground text-xs">
                      {formatPhone(membership.user?.phoneNumber)}
                    </p>
                  </Row>

                  <Row label={t('manageSubscriptions.plan')}>
                    <LocalizedName
                      nameEn={membership.subscriptionNameEn}
                      nameEs={membership.subscriptionNameEs}
                      internalName={membership.subscriptionInternalName}
                    />
                    <p className="text-muted-foreground text-xs">
                      {localizedName(
                        {
                          nameEn: membership.locationNameEn,
                          nameEs: membership.locationNameEs,
                        },
                        language
                      )}
                    </p>
                  </Row>

                  <Row label={t('manageSubscriptions.dates')}>
                    <p>
                      {formatDate(membership.startDate)} –{' '}
                      {formatDate(membership.endDate)}
                    </p>
                    <p className="text-muted-foreground text-xs">
                      {membership.cancelledAt
                        ? `${t('manageSubscriptions.cancelled')} ${formatDate(membership.cancelledAt)}`
                        : (membership.daysRemaining ?? 0) >= 0
                          ? t('manageSubscriptions.daysRemaining', {
                              count: membership.daysRemaining ?? 0,
                            })
                          : t('manageSubscriptions.expired')}
                    </p>
                  </Row>

                  <Row label={t('manageSubscriptions.originLabel')}>
                    <Badge variant="secondary">
                      {t(originLabelKey(membership.origin))}
                    </Badge>
                    {membership.assignedByName && (
                      <span className="text-muted-foreground ml-2 text-xs">
                        {membership.assignedByName}
                      </span>
                    )}
                    {membership.isMemberSeat && membership.ownerName && (
                      <p className="text-muted-foreground text-xs">
                        {t('manageSubscriptions.coveredBy')}:{' '}
                        {membership.ownerName}
                      </p>
                    )}
                  </Row>

                  <Row label={t('manageSubscriptions.money')}>
                    <p>
                      <span className="font-semibold tabular-nums">
                        {formatCurrency(membership.paidTotal)}
                      </span>
                      <span className="text-muted-foreground ml-2 text-xs">
                        {t('manageSubscriptions.listPrice')}:{' '}
                        {formatCurrency(membership.listPrice)}
                      </span>
                    </p>
                    {/* Muted and uncoloured on purpose: a lower amount is a
                        discount that was granted, not a debt. */}
                    {!membership.isMemberSeat &&
                      (membership.amountVariance ?? 0) < 0 && (
                        <p className="text-muted-foreground text-xs tabular-nums">
                          {formatCurrency(Math.abs(membership.amountVariance!))}{' '}
                          {t('manageSubscriptions.underListPrice')}
                        </p>
                      )}
                    {(membership.refundedTotal ?? 0) > 0 && (
                      <p className="text-muted-foreground text-xs tabular-nums">
                        {t('manageSubscriptions.refunded')}:{' '}
                        {formatCurrency(membership.refundedTotal)}
                      </p>
                    )}
                  </Row>

                  <Row label={t('manageSubscriptions.createdAt')}>
                    {formatDateTime(membership.createdAt)}
                  </Row>
                  <Row label={t('manageSubscriptions.autoRenew')}>
                    {membership.autoRenew ? t('common.yes') : t('common.no')}
                  </Row>
                  <Row label={t('manageSubscriptions.countForLimit')}>
                    {membership.countForLimit ? t('common.yes') : t('common.no')}
                  </Row>
                  {membership.comments && (
                    <Row label={t('manageSubscriptions.comments')}>
                      {membership.comments}
                    </Row>
                  )}
                </div>
              </TabsContent>

              {/* ----------------------------------------------- payments */}
              <TabsContent value="payments" className="space-y-3">
                {canManageMoney && !membership.isMemberSeat && (
                  <div className="flex justify-end">
                    <Button
                      size="sm"
                      onClick={() => onRecordPayment(membership)}
                    >
                      <CreditCard className="size-3.5" />
                      {t('manageSubscriptions.recordPayment')}
                    </Button>
                  </div>
                )}

                {payments.length === 0 ? (
                  <EmptyList>{t('manageSubscriptions.noPayments')}</EmptyList>
                ) : (
                  <div className="space-y-2">
                    {payments.map((payment) => (
                      <div
                        key={payment.id}
                        className="bg-card space-y-2 rounded-xl border p-3"
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <p className="text-sm">
                              {formatDateTime(payment.createdAt)}
                            </p>
                            {payment.isOffline && payment.recordedByName && (
                              <p className="text-muted-foreground text-xs">
                                {t('manageSubscriptions.recordedBy')}{' '}
                                {payment.recordedByName}
                              </p>
                            )}
                          </div>
                          <p className="font-semibold tabular-nums">
                            {formatCurrency(payment.amount)}
                          </p>
                        </div>

                        <div className="flex flex-wrap items-center gap-1.5">
                          <Badge
                            variant={payment.isOffline ? 'warning' : 'default'}
                          >
                            {t(paymentMethodLabelKey(payment.method ?? 0))}
                          </Badge>
                          <PaymentBadge payment={payment} />
                          {payment.isRenewal && (
                            <Badge variant="outline">
                              {t('manageSubscriptions.renewal')}
                            </Badge>
                          )}
                        </div>

                        {(payment.offlineReference || payment.adminNotes) && (
                          <p className="text-muted-foreground text-xs">
                            {[payment.offlineReference, payment.adminNotes]
                              .filter(Boolean)
                              .join(' · ')}
                          </p>
                        )}

                        <div className="flex justify-end">
                          <PaymentAction payment={payment} />
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </TabsContent>

              {/* ----------------------------------------------- benefits */}
              <TabsContent value="benefits" className="space-y-5">
                <div className="space-y-2">
                  <p className="text-muted-foreground text-xs font-semibold tracking-wider uppercase">
                    {t('manageSubscriptions.remainingBenefits')}
                  </p>
                  {benefits.length === 0 ? (
                    <EmptyList>{t('manageSubscriptions.noBenefits')}</EmptyList>
                  ) : (
                    <div className="divide-y rounded-xl border">
                      {benefits.map((benefit) => (
                        <div
                          key={benefit.id}
                          className="flex items-center justify-between gap-3 p-3"
                        >
                          <div className="min-w-0 text-sm">
                            <LocalizedName
                              nameEn={benefit.nameEn}
                              nameEs={benefit.nameEs}
                              internalName={benefit.internalName}
                            />
                          </div>
                          <div className="flex shrink-0 gap-5 text-right text-xs">
                            <div>
                              <p className="text-muted-foreground">
                                {t('manageSubscriptions.included')}
                              </p>
                              <p className="tabular-nums">
                                {benefit.totalLimit
                                  ? formatNumber(benefit.totalLimit)
                                  : t('manageSubscriptions.unlimited')}
                              </p>
                            </div>
                            <div>
                              <p className="text-muted-foreground">
                                {t('manageSubscriptions.remaining')}
                              </p>
                              <p className="tabular-nums">
                                {benefit.limit
                                  ? formatNumber(benefit.limit)
                                  : '—'}
                              </p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="space-y-2">
                  <p className="text-muted-foreground text-xs font-semibold tracking-wider uppercase">
                    {t('manageSubscriptions.benefitRedemptions')}
                  </p>
                  {redemptions.length === 0 ? (
                    <EmptyList>
                      {t('manageSubscriptions.noRedemptions')}
                    </EmptyList>
                  ) : (
                    <div className="divide-y rounded-xl border">
                      {redemptions.map((redemption) => (
                        <div
                          key={redemption.id}
                          className="flex items-center justify-between gap-3 p-3 text-sm"
                        >
                          <p className="min-w-0">
                            {localizedName(redemption.benefit, language)}
                          </p>
                          <p className="text-muted-foreground shrink-0 text-xs">
                            {formatDateTime(redemption.createdAt)}
                          </p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </TabsContent>

              {/* ----------------------------------------------- bookings */}
              <TabsContent value="bookings">
                {bookings.length === 0 ? (
                  <EmptyList>
                    {t('manageSubscriptions.noBookingsWithSubscription')}
                  </EmptyList>
                ) : (
                  <div className="divide-y rounded-xl border">
                    {bookings.map((booking) => (
                      <div
                        key={booking.id}
                        className="flex items-start justify-between gap-3 p-3"
                      >
                        <div className="min-w-0 space-y-1">
                          <p className="text-sm font-medium">
                            {booking.eventName}
                          </p>
                          <p className="text-muted-foreground text-xs">
                            {booking.locationName} ·{' '}
                            {formatDateTime(booking.timeslotStartTime)}
                          </p>
                          <p className="text-muted-foreground text-xs">
                            {localizedName(
                              {
                                nameEn: booking.benefitNameEn,
                                nameEs: booking.benefitNameEs,
                              },
                              language
                            )}
                          </p>
                          {booking.checkedInAt ? (
                            <Badge variant="success">
                              {t('manageSubscriptions.checkedIn')}
                            </Badge>
                          ) : (
                            <Badge variant="secondary">
                              {t('manageSubscriptions.notCheckedIn')}
                            </Badge>
                          )}
                        </div>
                        {/* Value given away, never money collected. Muted so it
                            cannot be misread as revenue. */}
                        <div className="shrink-0 text-right">
                          <p className="text-muted-foreground text-xs">
                            {t('manageSubscriptions.benefitValue')}
                          </p>
                          <p className="text-muted-foreground text-sm tabular-nums">
                            {formatCurrency(booking.benefitValue)}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </TabsContent>

              {/* ------------------------------------------------- family */}
              {members.length > 0 && (
                <TabsContent value="family">
                  <div className="divide-y rounded-xl border">
                    {members.map((member) => (
                      <div
                        key={member.userId ?? member.email}
                        className="flex items-center justify-between gap-3 p-3"
                      >
                        <div className="min-w-0">
                          <p className="truncate text-sm">
                            {[member.firstName, member.lastName]
                              .filter(Boolean)
                              .join(' ')}
                          </p>
                          <p className="text-muted-foreground truncate text-xs">
                            {member.email}
                          </p>
                          <p className="text-muted-foreground text-xs">
                            {t('manageSubscriptions.createdAt')}:{' '}
                            {formatDate(member.createdAt)}
                          </p>
                        </div>
                        <Badge
                          variant={member.isActive ? 'success' : 'destructive'}
                        >
                          {member.isActive
                            ? t('manageSubscriptions.active')
                            : t('manageSubscriptions.inactive')}
                        </Badge>
                      </div>
                    ))}
                  </div>
                </TabsContent>
              )}
            </Tabs>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
